/**
 * Dashboard Link Merge Utilities
 *
 * This module provides declarative rules for merging multiple Nexa tool calls
 * into single dashboard links. Supports:
 * - Same interval dimension expansion (merge calls with same interval/filter/granularity)
 * - DoD comparison detection (merge calls with same duration into comparison view)
 */

import moment from 'moment';
import { uniq, flatMap, isEqual, intersection } from 'lodash';
import { logger } from '@src/utils/misc/logger';
import { getFilterFromRules, getRulesFromFilter } from '@src/utils';
import { fixFilterFieldNames } from '../utils';

// ============================================================================
// Types
// ============================================================================

// Tool names that can be merged for trends dashboards
export const TRENDS_TOOL_NAMES = ['metrics_query']; // 'metric_filter', 'flow_query'

// Raw function call from Nexa events (only contains id and name, not full args)
export interface RawFunctionCall {
  id: string;
  value: {
    id: string; // adk-... id used to look up config in dashboardConfigMap
    name: string; // tool name like 'metrics_query'
  };
}

// Full config from dashboardConfigMap (looked up by adk id)
export interface DashboardConfig {
  tool_name?: string;
  args?: ToolCallArgs;
  parent_call_id?: string;
  [key: string]: any;
}

// Function call with resolved config (after lookup)
export interface ResolvedFunctionCall {
  id: string; // event id
  adkId: string; // adk-... id
  toolName: string;
  args: ToolCallArgs;
}

// Arguments structure from tool calls
// Includes both original tool args and derived keys from merge operations
export interface ToolCallArgs {
  metrics?: string[];
  groupBy?: string[];
  interval?: string;
  granularity?: string;
  filter?: any[];
  metricFilter?: any[];
  flowId?: string;
  sortBy?: Array<{ order: string; metric: string }>;
  // Derived keys from merge operations
  comparisonInterval?: string; // For DoD merged links
  compareFilters?: CompareFilter[]; // For filter comparison merged links
  [key: string]: any;
}

// Compare filter structure for filter comparison merge
export interface CompareFilter {
  name: string;
  rule: any[][];
  meta: {
    cid: string;
    [key: string]: any;
  };
}

// Result of merging multiple tool calls
// Note: derived keys like comparisonInterval are stored inside args for self-contained configs
export interface MergedConfig {
  type: 'merged' | 'dod-merged' | 'filter-compare-merged';
  sourceIds: string[];
  args: ToolCallArgs;
}

// Merge rule definition
export interface MergeRule {
  name: string;
  applicableTools: string[];
  canMerge: (a: ResolvedFunctionCall, b: ResolvedFunctionCall) => boolean;
  merge: (calls: ResolvedFunctionCall[]) => MergedConfig;
}

// Result of groupAndMergeTrendsLinks
export interface MergeResult {
  merged: MergedConfig[];
  standalone: RawFunctionCall[];
}

// Type for dashboardConfigMap (lookup by adk id)
export type DashboardConfigMap = Record<string, DashboardConfig>;

// ============================================================================
// Constants
// ============================================================================

/**
 * Default tolerance in seconds for comparing interval timestamps
 * Handles cases where intervals are generated at slightly different times
 * but represent the same logical time range (e.g., "last 24 hours")
 */
const INTERVAL_TOLERANCE_SECONDS = 60;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Parse ISO interval string into moment objects
 */
const parseMergeInterval = (interval: string): { from: moment.Moment; to: moment.Moment } => {
  const [from, to] = interval.split('/');
  return { from: moment(from), to: moment(to) };
};

/**
 * Check if two intervals have the same duration (within tolerance)
 */
const intervalDurationsMatch = (a: string, b: string, toleranceMinutes = 1): boolean => {
  try {
    const intA = parseMergeInterval(a);
    const intB = parseMergeInterval(b);
    const diffA = intA.to.diff(intA.from, 'minutes');
    const diffB = intB.to.diff(intB.from, 'minutes');
    return Math.abs(diffA - diffB) <= toleranceMinutes;
  } catch {
    return false;
  }
};

/**
 * Check if two intervals are practically the same (start and end within tolerance)
 * This handles cases where intervals are generated at slightly different times
 * but represent the same logical time range (e.g., "last 24 hours")
 */
const intervalsAreSame = (
  a: string,
  b: string,
  toleranceSeconds = INTERVAL_TOLERANCE_SECONDS,
): boolean => {
  // Fast path: exact match
  if (a === b) return true;

  try {
    const intA = parseMergeInterval(a);
    const intB = parseMergeInterval(b);
    const startDiff = Math.abs(intA.from.diff(intB.from, 'seconds'));
    const endDiff = Math.abs(intA.to.diff(intB.to, 'seconds'));
    return startDiff <= toleranceSeconds && endDiff <= toleranceSeconds;
  } catch {
    return false;
  }
};

/**
 * Check if two filter arrays are equal
 */
const filtersMatch = (a: any[] | undefined, b: any[] | undefined): boolean => {
  return isEqual(a || [], b || []);
};

/**
 * Check if two metric arrays have at least one common element
 */
const metricsOverlap = (a: string[] | undefined, b: string[] | undefined): boolean => {
  if (!a || !b || a.length === 0 || b.length === 0) return false;
  return intersection(a, b).length > 0;
};

/**
 * Check if groupBy is empty (timeseries query)
 */
const isTimeseries = (groupBy: string[] | undefined): boolean => {
  return !groupBy || groupBy.length === 0;
};

/**
 * Generate a simple random ID for filter meta.cid
 */
const generateFilterCid = (): string => {
  return Math.random().toString(36).substring(2, 15);
};

/**
 * Convert tool call filter to compareFilter format
 * Uses fixFilterFieldNames -> getRulesFromFilter -> getFilterFromRules
 */
const convertToCompareFilter = (filter: any[] | undefined): CompareFilter | null => {
  if (!filter || filter.length === 0) {
    return null;
  }

  try {
    // Ensure filter has proper display names
    const fixedFilter = fixFilterFieldNames(filter);
    // Convert to rules map format
    const rulesMap = getRulesFromFilter(fixedFilter, true);
    // Convert to final filter object with name and rule
    const filterObj = getFilterFromRules(rulesMap, true);

    return {
      name: filterObj.name || 'Filter',
      rule: filterObj.rule || fixedFilter,
      meta: {
        cid: generateFilterCid(),
      },
    };
  } catch (error) {
    logger.log('[MERGE] Error converting filter to compareFilter:', error);
    return null;
  }
};

/**
 * Resolve a raw function call by looking up its config from the map
 */
const resolveRawFunctionCall = (
  raw: RawFunctionCall,
  configMap: DashboardConfigMap,
): ResolvedFunctionCall | null => {
  const adkId = raw.value?.id;
  if (!adkId) return null;

  const config = configMap[adkId];
  if (!config) return null;

  return {
    id: raw.id,
    adkId,
    toolName: config.tool_name || raw.value.name || '',
    args: config.args || {},
  };
};

/**
 * Group items by mergeability using a pairwise comparison function
 * Returns groups where all items in a group can be merged together
 * Note: Uses adkId for tracking because multiple events can share the same event id
 */
const groupByMergeability = (
  items: ResolvedFunctionCall[],
  canMerge: (a: ResolvedFunctionCall, b: ResolvedFunctionCall) => boolean,
): ResolvedFunctionCall[][] => {
  const groups: ResolvedFunctionCall[][] = [];
  const used = new Set<string>();

  for (const item of items) {
    // Use adkId for tracking - multiple events can share the same event id
    if (used.has(item.adkId)) continue;

    const group: ResolvedFunctionCall[] = [item];
    used.add(item.adkId);

    for (const other of items) {
      if (used.has(other.adkId)) continue;

      // Check if this item can merge with all items already in the group
      const canMergeWithGroup = group.every((groupItem) => canMerge(groupItem, other));

      if (canMergeWithGroup) {
        group.push(other);
        used.add(other.adkId);
      }
    }

    groups.push(group);
  }

  return groups;
};

// ============================================================================
// Merge Rules
// ============================================================================

/**
 * Rule 1: Same Interval Dimension Expansion
 * Merges calls with same interval, filter, and granularity
 * Combines both metrics and dimensions arrays (deduplicated)
 */
const sameIntervalDimensionRule: MergeRule = {
  name: 'same-interval-dimension-expansion',
  applicableTools: ['metrics_query'],
  canMerge: (a, b) => {
    // Must have intervals defined
    if (!a.args.interval || !b.args.interval) return false;
    // Must have same interval (within tolerance for slight timing differences)
    if (!intervalsAreSame(a.args.interval, b.args.interval)) return false;
    // Must have same filter
    if (!filtersMatch(a.args.filter, b.args.filter)) return false;
    // Must have same granularity (treat undefined as 'ALL')
    if ((a.args.granularity || 'ALL') !== (b.args.granularity || 'ALL')) return false;
    return true;
  },
  merge: (calls) => ({
    type: 'merged',
    sourceIds: calls.map((c) => c.adkId), // Use adkId for source tracking
    args: {
      ...calls[0].args,
      metrics: uniq(flatMap(calls, (c) => c.args.metrics || [])),
      groupBy: uniq(flatMap(calls, (c) => c.args.groupBy || [])),
    },
  }),
};

/**
 * Rule 2: DoD Comparison Detection
 * Detects two calls with same duration (within 1 min tolerance), same filter/granularity
 * Sets primary interval (later one) and comparison interval (earlier one)
 */
const dodComparisonRule: MergeRule = {
  name: 'dod-comparison',
  applicableTools: ['metrics_query'],
  canMerge: (a, b) => {
    // Must have same filter
    if (!filtersMatch(a.args.filter, b.args.filter)) return false;
    // Must have same granularity
    if ((a.args.granularity || 'ALL') !== (b.args.granularity || 'ALL')) return false;
    // Must have intervals defined
    if (!a.args.interval || !b.args.interval) return false;
    // Intervals must NOT be the same (otherwise use sameIntervalDimensionRule)
    // Use tolerance-based check to properly exclude "same interval" cases
    if (intervalsAreSame(a.args.interval, b.args.interval)) return false;
    // Check duration match with 1 minute tolerance
    return intervalDurationsMatch(a.args.interval, b.args.interval, 1);
  },
  merge: (calls) => {
    // Sort by interval start time, later one is primary
    const sorted = [...calls].sort((a, b) => {
      const aFrom = moment(a.args.interval?.split('/')[0]);
      const bFrom = moment(b.args.interval?.split('/')[0]);
      return bFrom.diff(aFrom);
    });

    return {
      type: 'dod-merged',
      sourceIds: calls.map((c) => c.adkId), // Use adkId for source tracking
      args: {
        ...sorted[0].args,
        metrics: uniq(flatMap(calls, (c) => c.args.metrics || [])),
        groupBy: uniq(flatMap(calls, (c) => c.args.groupBy || [])),
        // Store comparison interval inside args for self-contained config
        comparisonInterval: sorted[1].args.interval,
      },
    };
  },
};

/**
 * Rule 3: Filter Comparison
 * Merges timeseries calls with same interval/metrics/granularity but different filters
 * Creates a dashboard with compareFilters enabled
 */
const filterComparisonRule: MergeRule = {
  name: 'filter-comparison',
  applicableTools: ['metrics_query'],
  canMerge: (a, b) => {
    // Must have intervals defined
    if (!a.args.interval || !b.args.interval) return false;
    // Must have same interval (within tolerance)
    if (!intervalsAreSame(a.args.interval, b.args.interval)) return false;
    // Must have metrics that overlap
    if (!metricsOverlap(a.args.metrics, b.args.metrics)) return false;
    // Must have same granularity (both should be timeseries, not 'ALL')
    const granA = a.args.granularity || 'ALL';
    const granB = b.args.granularity || 'ALL';
    if (granA !== granB) return false;
    // Must be timeseries (empty groupBy)
    if (!isTimeseries(a.args.groupBy) || !isTimeseries(b.args.groupBy)) return false;
    // Filters must be different (this is what makes it a filter comparison)
    if (filtersMatch(a.args.filter, b.args.filter)) return false;
    // Both must have non-empty filters (comparing "All Traffic" vs specific filter is less useful)
    if (!a.args.filter || a.args.filter.length === 0) return false;
    if (!b.args.filter || b.args.filter.length === 0) return false;
    return true;
  },
  merge: (calls) => {
    // Convert each call's filter to compareFilter format
    const compareFilters: CompareFilter[] = [];
    for (const call of calls) {
      const compareFilter = convertToCompareFilter(call.args.filter);
      if (compareFilter) {
        compareFilters.push(compareFilter);
      }
    }

    // Use first call as base, combine metrics
    const baseCall = calls[0];

    return {
      type: 'filter-compare-merged',
      sourceIds: calls.map((c) => c.adkId),
      args: {
        ...baseCall.args,
        metrics: uniq(flatMap(calls, (c) => c.args.metrics || [])),
        groupBy: [], // Ensure empty for timeseries
        filter: [], // Clear the base filter since we're using compareFilters
        compareFilters,
      },
    };
  },
};

// Rules in priority order (most specific first)
// - DoD: different intervals, same duration
// - Filter comparison: same interval, different filters, timeseries only
// - Same interval dimension: same interval/filter, combines dimensions
const MERGE_RULES: MergeRule[] = [
  dodComparisonRule,
  filterComparisonRule,
  sameIntervalDimensionRule,
];

// ============================================================================
// Main Export
// ============================================================================

/**
 * Groups and merges trends-related function calls based on declarative rules
 * Returns merged configs and standalone function calls
 *
 * @param rawFunctionCalls - Raw function calls from events (contain adk id and name)
 * @param configMap - Dashboard config map to look up full args by adk id
 */
export const groupAndMergeTrendsLinks = (
  rawFunctionCalls: RawFunctionCall[],
  configMap: DashboardConfigMap,
): MergeResult => {
  // Resolve raw calls by looking up their configs
  const resolvedCalls: ResolvedFunctionCall[] = [];
  const unresolvedCalls: RawFunctionCall[] = [];

  for (const raw of rawFunctionCalls) {
    const resolved = resolveRawFunctionCall(raw, configMap);
    if (resolved) {
      resolvedCalls.push(resolved);
    } else {
      unresolvedCalls.push(raw);
    }
  }

  // Filter to only trends-related tools
  const trendsCalls = resolvedCalls.filter((fc) => TRENDS_TOOL_NAMES.includes(fc.toolName));
  const otherCalls = resolvedCalls.filter((fc) => !TRENDS_TOOL_NAMES.includes(fc.toolName));

  // Map resolved calls back to raw for standalone output (keyed by adkId)
  const resolvedToRaw = new Map<string, RawFunctionCall>();
  for (const raw of rawFunctionCalls) {
    if (raw.value?.id) {
      resolvedToRaw.set(raw.value.id, raw);
    }
  }

  // If 0 or 1 trends calls, nothing to merge
  if (trendsCalls.length <= 1) {
    return { merged: [], standalone: rawFunctionCalls };
  }

  // Try each rule in priority order
  for (const rule of MERGE_RULES) {
    const applicable = trendsCalls.filter((fc) => rule.applicableTools.includes(fc.toolName));

    if (applicable.length <= 1) {
      continue;
    }

    // Group calls that can be merged together
    const groups = groupByMergeability(applicable, rule.canMerge);

    // Find groups with more than one item (mergeable)
    const mergeableGroups = groups.filter((g) => g.length > 1);

    if (mergeableGroups.length > 0) {
      // Apply the merge to each group
      const mergedConfigs = mergeableGroups.map((group) => rule.merge(group));

      // Collect adk IDs that were merged (use adkId since event ids can be duplicated)
      const mergedAdkIds = new Set(mergeableGroups.flatMap((g) => g.map((c) => c.adkId)));

      // Remaining trends calls that weren't merged (as raw)
      const remainingTrends = trendsCalls
        .filter((fc) => !mergedAdkIds.has(fc.adkId))
        .map((fc) => resolvedToRaw.get(fc.adkId)!)
        .filter(Boolean);

      // Other calls (non-trends) as raw
      const otherRaw = otherCalls.map((fc) => resolvedToRaw.get(fc.adkId)!).filter(Boolean);

      logger.log(`[MERGE] Applied ${rule.name}`, {
        merged: mergedConfigs.length,
        standalone: remainingTrends.length + otherRaw.length + unresolvedCalls.length,
      });

      return {
        merged: mergedConfigs,
        standalone: [...remainingTrends, ...otherRaw, ...unresolvedCalls],
      };
    }
  }

  // No merges possible
  return { merged: [], standalone: rawFunctionCalls };
};
